import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Button, Row } from "antd";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import Layout from "../../../components/Layout";
import { customBaseUrl, httpGet, httpPost } from "../../../store/http";
import { Grid } from "@mui/material";
import AntDropdown from "../../../components/CustomDropdown/AntDropdown";
import { useMatStyles } from "./style";
import { hideLoader, showLoader } from "../../../utils/loader";
import { FilterList, Settings } from "@mui/icons-material";
import ReferralTypes from "./referalTypes";
import ReferredUsers from "./referres";
import noDataLogo from "../../../assets/images/no-data.png";
import { CSVLink } from "react-csv";
import ExportData from "./exportData";
import AssignCode from "./assignCode";
import RefferalWebForm from "./webForm";
import UploadExcel from "./excelUpload";
import SingleUserBonus from "./singleUserBonus";
import UpdateUserReferral from "./updateUserReferral";

const ManageRefferrals = () => {
  const navigate = useNavigate();
  const [showRefTypes, setShowRefTypes] = useState(false);
  const [pagination, setPagination] = useState({
    current: 0,
    pageSize: 10,
  });
  const styles = useMatStyles();
  const [search] = useState({
    searchBy: "referralUser",
    searchText: "",
  });
  const [allTransfers, setAllTransfers] = useState(0);
  const [allReferrals, setAllreferrals] = useState([]);
  const [allReferralTypes, setAllreferralTypes] = useState([]);
  const [selectedRow, setSelectedRow] = useState({});
  const [fileteredMeta] = useState(0);
  const [stat, setStat] = useState([]);
  const [searchField, setSearchField] = useState(false);
  const [field, setField] = useState('all');
  const [value, setValue] = useState('all');
  const [isReferrers, setIsReferrers] = useState(false);
  const [filterValue] = useState(false);

  // Missing states
  const [showExport, setShowExport] = useState(false);
  const [showAssignCode, setShowAssignCode] = useState(false);
  const [showMultiple, setShowMultiple] = useState(false);
  const [showExcel, setShowExcel] = useState(false);
  const [showSendSingle, setShowSendSingle] = useState(false);
  const [showUpdateReferral, setShowUpdateReferral] = useState(false);
  const [filteredData, setFilteredData] = useState({});

  const getTransactions = useCallback(async (page, size) => {
    setAllreferrals([]);
    showLoader();
    const pages = page ?? pagination.current;
    const limit = size ?? pagination.pageSize;
    let request = await httpPost(
      `${customBaseUrl.referralUrl}/api/v1/admin/fetchAllReferrals`,
      {
        pageSize: limit,
        pageNo: pages
      }
    );
    if (request.status) {
      setAllreferrals(request.data.profile || []);
      setAllTransfers(request.data.totalItems || 0);
    }
    hideLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStat = useCallback(async () => {
    let request = await httpGet(`${customBaseUrl.referralUrl}/api/v1/commission_report/analysis`);
    if (request.status) {
      setStat(request?.data || []);
    }
    hideLoader();
  }, []);

  useEffect(() => {
    getStat();
    getTransactions();
  }, [getStat, getTransactions]);

  const handleChangeRowsPerPage = async (value) => {
    setPagination({ ...pagination, current: 0, pageSize: value });
    await getTransactions(0, value);
  };

  const handleChangePage = async (newPage) => {
    setPagination({ ...pagination, current: newPage });
    await getTransactions(newPage, pagination.pageSize);
  };

  const handleDropdownAction = useCallback((item, row) => {
    setSelectedRow(row);
    if (item === "VIEW MORE") navigate(`/referrals/${row?.referenceCode}`);
    if (item === "CHANGE REFERRAL") setShowUpdateReferral(true);
  }, [navigate]);

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'Referrer Name',
      width: 200,
      headerClassName: styles.tableHeader,
    },
    {
      field: 'referralName',
      headerName: 'Referral Name',
      width: 200,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.referralName ? row?.referralName : 'N/A'
    },
    {
      field: 'referralType',
      headerName: 'Referral Type',
      width: 200,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.referralType ? row?.referralType?.name : 'N/A'
    },
    {
      field: 'totalReferral',
      headerName: 'Total Referral',
      width: 200,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.totalReferral ? row?.totalReferral : 'N/A'
    },
    {
      field: 'phoneNumber',
      headerName: 'Referrer Phone',
      width: 250,
      headerClassName: styles.tableHeader,
    },
    {
      field: 'email',
      headerName: "Referrer Email",
      width: 200,
      headerClassName: styles.tableHeader,
    },
    {
      field: 'RphoneNumber',
      headerName: 'Referral Phone',
      width: 250,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.referral ? row?.referral.phoneNumber : 'N/A'
    },
    {
      field: 'remail',
      headerName: "Referral Email",
      width: 200,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.referral ? row?.referral.email : 'N/A'
    },
    {
      field: 'createdAt',
      headerName: "Referrer Account Creation Date",
      width: 200,
      headerClassName: styles.tableHeader,
    },
    {
      field: 'rcreatedAt',
      headerName: 'Referral Account Creation Date',
      width: 250,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.referral ? row?.referral.createdAt : 'N/A'
    },
    {
      field: 'active',
      headerName: "Status",
      width: 150,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.active ? <div style={{ color: 'green' }}>Active</div> : <div style={{ color: 'red' }}>Inactive</div>
    },
    {
      field: 'corporate',
      headerName: "Account Type",
      width: 150,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) => row?.corporate ? 'Corporate' : 'Personal'
    },
    {
      field: 'bonus',
      headerName: "Action ",
      width: 150,
      headerClassName: styles.tableHeader,
      renderCell: ({ row }) =>
        <AntDropdown
          dropDownOptions={["VIEW MORE", "CHANGE REFERRAL"]}
          handleDropdownAction={(item) => handleDropdownAction(item, row)}
          userId={row}
          isArrow
        />
    }
  ], [styles.tableHeader, handleDropdownAction]);

  const exportData = useMemo(() => allReferrals.map((el) => ({
    "Referrer Referral Code": el?.referenceCode,
    "Referral Referral Code": el?.referral ? el?.referral.referralCode : '',
    "Referrer Name": el.name,
    "Referral Name": el?.referral ? el?.referral.name : '',
    "Referrer Phone": el?.phoneNumber?.toString(),
    "Referrer Email": el.email,
    "Referral Phone": el?.referral ? el?.referral.phoneNumber?.toString() : '',
    "referrer Account Creation Date": el.createdAt,
    "referral Account Creation Date": el?.referral ? el?.referral.createdAt : '',
    "Status": el.active,
    'Account Type': el?.corporate ? 'Corporate' : 'Personal',
  })), [allReferrals]);

  const category = ['all', 'name', 'email', 'phoneNumber', 'referenceCode']

  const getReferralTypes = useCallback(async () => {
    setAllreferralTypes([]);
    showLoader();
    let request = await httpPost(
      `${customBaseUrl.referralUrl}/api/v1/referral_type/view_report`,
      {
        pageSize: 100,
        pageNo: 0,
      }
    );
    if (request.status) {
      setAllreferralTypes(request.data.content || []);
    }
    hideLoader();
  }, []);

  useEffect(() => {
    getReferralTypes();
  }, [getReferralTypes]);

  const getSearchRef = async (e) => {
    e.preventDefault();
    getTransactions();
  };

  const gridStyle = {
    width: "40%",
    textAlign: "left",
    backgroundColor: "#fff",
    padding: "24px 10px 24px 12px",
    fontWeight: "bold",
    marginLeft: "50px",
  };

  return (
    <Layout>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
        <div />
        <Row type="flex" justify="end" align="middle">
          <Button
            onClick={() => setShowRefTypes(true)}
            size="medium"
            style={{ fontSize: "14px", }}
          >
            <Row>
              <Settings />
              Referral Settings
            </Row>
          </Button>
        </Row>
      </div>
      <Row>
        <Button
          variant="contained"
          style={{ marginRight: 20, backgroundColor: !isReferrers ? '#ff6700' : '' }}
          onClick={() => setIsReferrers(false)}
        >
          Aggregators
        </Button>
        <Button
          variant="contained"
          style={{ marginRight: 20, backgroundColor: isReferrers ? '#ff6700' : '' }}
          onClick={() => setIsReferrers(true)}
        >
          Referrals
        </Button>
      </Row>
      {showRefTypes ?
        <ReferralTypes showRefTypes={showRefTypes} setShowRefTypes={setShowRefTypes} />
        :
        isReferrers ? <ReferredUsers /> :
          <div>
            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', marginTop: '10px' }}>
              <FilterList
                onClick={() => {
                  setSearchField(!searchField)
                  setField('all')
                  if (searchField) getTransactions();
                }}
              />Filter
            </div>
            <Row type="flex" justify="end" align="middle">
              <Button variant="contained" style={{ marginRight: 20 }}>
                <CSVLink
                  data={exportData}
                  style={{ display: "flex", alignItems: "center", textDecoration: "none" }}
                  filename={`Referrals.csv`}
                >
                  DOWNLOAD AS EXCEL
                </CSVLink>
              </Button>
            </Row>
            <div hidden={!searchField}>
              <form onSubmit={getSearchRef}>
                <div className="row">
                  <div className="input-group mb-3 col">
                    <span className="input-group-text" id="basic-addon3">Field</span>
                    <select className="form-select" id="inputGroupSelect01" required
                      value={field}
                      onChange={(e) => {
                        setField(e.target.value);
                        setFilteredData({ ...filteredData, status: e?.target?.value })
                        setPagination({ ...pagination, current: 0 })
                      }}
                    >
                      <option value="">Choose...</option>
                      {category?.map((e, i) =>
                        <option key={i} value={e}>{e.replace('_', ' ').toUpperCase()}</option>
                      )}
                    </select>
                  </div>
                  <div className="input-group mb-3 col">
                    <span className="input-group-text" id="basic-addon3">Value</span>
                    <input
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      className="form-select" id="inputGroupSelect02" />
                  </div>
                </div>

                <div className="col-12 text-center">
                  <button type="submit" className="btn btn-primary">Search</button>
                  <button
                    onClick={() => {
                      setField('all');
                      setSearchField(false);
                      getTransactions();
                    }}
                    type="button"
                    className="btn btn-danger mx-3"
                  >Clear</button>
                </div>
              </form>
            </div>

            {filterValue ?
              <Card style={{ background: "transparent" }}>
                <Card.Grid style={gridStyle} className="mr-2">
                  <p>Referral Code</p>
                  <h5 className="text-left">{search?.searchText}</h5>
                </Card.Grid>
                <Card.Grid hoverable={false} style={gridStyle} className="sm-1">
                  <p>Total Signup with Referral Code</p>
                  <h2 className="text-left">{fileteredMeta}</h2>
                </Card.Grid>
              </Card>
              : <Card style={{ background: "transparent" }}>
                <Card.Grid style={gridStyle} className="mr-2">
                  <p>Total Number of Referrals</p>
                  <h2 className="text-left">{stat?.totalReferralCount || 0}</h2>
                </Card.Grid>
                <Card.Grid style={gridStyle} className="mr-2">
                  <p>Total Number of Transaction of Referrals</p>
                  <h2 className="text-left">{stat?.totalTransactionCommission || 0}</h2>
                </Card.Grid>
              </Card>
            }

            <Grid
              style={{ marginTop: "10px" }}
              container
              spacing={1}
              columnSpacing={{ xs: 1, sm: 2, md: 4 }}
            />

            {allReferrals.length > 0 ? (
              <div style={{ height: 800, width: "100%" }}>
                <DataGrid
                  getRowId={row => row.userId || row.id}
                  className={styles.table}
                  density="comfortable"
                  rows={allReferrals}
                  columns={columns}
                  page={pagination.current}
                  paginationMode="server"
                  pageSize={pagination.pageSize}
                  onPageSizeChange={handleChangeRowsPerPage}
                  onPageChange={handleChangePage}
                  rowsPerPageOptions={[5, 10, 20, 50, 100]}
                  pagination
                  components={{ Toolbar: GridToolbar }}
                  rowCount={allTransfers}
                />
              </div>
            ) : (
              <>
                <Row
                  type="flex"
                  justify="center"
                  align="middle"
                  className="mt-5"
                  style={{ minHeight: "40vh" }}
                >
                  <img src={noDataLogo} alt="nodata" style={{ height: "300px" }} />
                </Row>
                <h4 className="mt-3 text-center">No Request yet</h4>
                <p className="mt-3 text-center" style={{ color: "#8C8C8C" }}>
                  Request will appear here when you have data
                </p>
              </>
            )}
          </div>
      }

      {showExport && <ExportData showModal={showExport} hideModal={setShowExport} dataList={allReferrals} />}
      {showAssignCode && <AssignCode showModal={showAssignCode} hideModal={setShowAssignCode} title="Assign Referral Code" className={styles.modal} />}
      {showMultiple && <RefferalWebForm showModal={showMultiple} hideModal={setShowMultiple} />}
      {showExcel && <UploadExcel showModal={showExcel} hideModal={setShowExcel} />}
      {showSendSingle && <SingleUserBonus showModal={showSendSingle} hideModal={setShowSendSingle} selectedData={selectedRow} />}
      {showUpdateReferral && <UpdateUserReferral
        showModal={showUpdateReferral}
        hideModal={setShowUpdateReferral}
        title="Update User Referral Type"
        allReferralTypes={allReferralTypes}
        row={selectedRow}
      />}
    </Layout>
  );
};

export default ManageRefferrals;